/**
 * Screen-security settings for the web client.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ScreenSecuritySettings.swift` —
 * same three booleans, same privacy-preserving defaults.
 *
 * PLATFORM NOTE: Browsers have no equivalent of:
 *   - iOS app-switcher snapshot blur (willResignActive + overlay)
 *   - UIScreen.capturedDidChangeNotification (partial — see ScreenCaptureDetectionPolicy)
 *   - UIApplicationUserDidTakeScreenshotNotification (HARD GAP — no web API)
 *
 * Web adaptations:
 *   - `blurOnHidden`          → clear/blur sensitive DOM on visibilitychange=hidden
 *   - `screenCaptureDetection`→ getDisplayMedia permission monitoring (limited; see docs)
 *   - `notifyPeerOnCapture`   → per-conversation opt-in; global flag CANNOT enable this
 *
 * See `kaether_for_web/docs/screen-security.md` for the full gap analysis.
 */

/**
 * Global screen-security settings.
 *
 * Defaults are privacy-preserving:
 *   - `blurOnHidden`:            true  — clear sensitive DOM when tab is hidden
 *   - `screenCaptureDetection`:  true  — attempt to detect ongoing screen sharing
 *   - `notifyPeerOnCapture`:     false — never notify the peer without explicit opt-in
 *
 * Matches the iOS defaults: blur ON, detection ON, notifyPeer OFF.
 */
export interface ScreenSecuritySettings {
  /**
   * When true, the app clears (or blurs) sensitive content (chat list, message
   * bodies) whenever the browser tab becomes hidden (`document.visibilityState
   * === 'hidden'`).
   *
   * iOS analogue: `AppSwitcherBlurPolicy.showOverlay` fired at `willResignActive`.
   *
   * Default: true.
   */
  readonly blurOnHidden: boolean;

  /**
   * When true, the app attempts to detect ongoing screen-sharing via
   * `getDisplayMedia` permission monitoring.
   *
   * iOS analogue: `UIScreen.capturedDidChangeNotification`.
   *
   * Note: This is a PARTIAL mitigation — browsers require the user to actively
   * start a screen share via `getDisplayMedia`; passive screen capture (e.g.
   * screen-recording by the OS) cannot be detected.
   *
   * Default: true.
   */
  readonly screenCaptureDetection: boolean;

  /**
   * This is a GLOBAL flag that, when false (the default), prevents any
   * notification-to-peer from being sent regardless of per-conversation settings.
   *
   * CRITICAL: A global `true` here does NOT enable notify-peer on its own.
   * Peer notification requires BOTH this flag AND a per-conversation
   * `ScreenCaptureConversationSetting.logAuditAndNotifyPeer` opt-in.
   *
   * iOS analogue: top-level `ScreenSecuritySettings.notifyPeerOnCapture` — the
   * iOS spec also requires per-conversation opt-in; global=true is only the
   * prerequisite gate.
   *
   * Default: false.
   */
  readonly notifyPeerOnCapture: boolean;
}

/** Privacy-preserving defaults — mirrors iOS ScreenSecuritySettings defaults. */
export const DEFAULT_SCREEN_SECURITY_SETTINGS: ScreenSecuritySettings = {
  blurOnHidden: true,
  screenCaptureDetection: true,
  notifyPeerOnCapture: false,
} as const;

/**
 * Merge a partial override onto the defaults. Partial fields not supplied
 * fall back to the defaults (same pattern as iOS `ScreenSecuritySettings.merging`).
 */
export function mergeScreenSecuritySettings(
  override: Partial<ScreenSecuritySettings>,
): ScreenSecuritySettings {
  return {
    blurOnHidden:
      override.blurOnHidden ?? DEFAULT_SCREEN_SECURITY_SETTINGS.blurOnHidden,
    screenCaptureDetection:
      override.screenCaptureDetection ??
      DEFAULT_SCREEN_SECURITY_SETTINGS.screenCaptureDetection,
    notifyPeerOnCapture:
      override.notifyPeerOnCapture ??
      DEFAULT_SCREEN_SECURITY_SETTINGS.notifyPeerOnCapture,
  };
}
